import { toDate } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { IconExternalLink } from "@suse-o11y/icons";
import { prop } from "@suse-o11y/prop";
import { RemoteData } from "@suse-o11y/remote-data";
import { Signal } from "@suse-o11y/signal";
import React from "react";
import { useMeasure } from "react-use";

import { RefreshIntervalSelector } from "~/components/refresh-interval-selector";
import type { RefreshInterval } from "~/components/refresh-interval-selector/utils";
import { SkeletonBox } from "~/components/skeleton/box";
import { SkeletonText } from "~/components/skeleton/text";
import type { MetricQuery } from "~/models/metric-query";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { useRefetchQueriesInNamespace } from "../../queries/use-refetch-queries-in-namespace";
import { METRICS_PATH } from "../../routes/Routes";
import { EmptyChart } from "../react/chart/chart-states/EmptyChart";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";
import { LinkTo } from "../react/link";
import { TimelineRangeSelectorExtended } from "../react/timeline/TimelineRangeSelectorExtended";
import { PaginatedChart } from "../widget-form/paginated";
import { fromMetricQuery as widgetQueryFromMetricQuery } from "../widget-form/query/spec";
import type { Spec as ChartSpec } from "../widget-form/spec";
import { useQueryTimeSeries } from "../widget-form/use-query-time-series";

import { QUERY_NAMESPACE } from "./_query-namespace";

export interface ContentModel {
	chart: ChartSpec;
	metricQueries: ReadonlyArray<MetricQuery>;
}

export interface ContentProps {
	className?: string;

	/**
	 * A timestamp to render marker in the timeseries chart.
	 */
	timestamp: undefined | Time;

	/**
	 * An initial TimeRange of the chart.
	 */
	timeRange: TimeRange;

	model: RemoteData<Error, ContentModel>;
}

export const Content: React.FC<ContentProps> = ({ className, model, timestamp, timeRange: initialTimeRange }) => {
	const [measureRef, { width: chartWidth }] = useMeasure<React.ComponentRef<"div">>();
	const [isRefetching, refetchQueries] = useRefetchQueriesInNamespace(QUERY_NAMESPACE);

	const [refreshInterval, setRefreshInterval] = React.useState<RefreshInterval>("off");

	const [timeRange, setTimeRange] = React.useState(initialTimeRange);
	const timeRangeSignal = React.useMemo(() => Signal<TimeRange>(() => timeRange, setTimeRange), [timeRange]);

	const result = useQueryTimeSeries({
		namespace: QUERY_NAMESPACE,
		timeRange,
		width: model
			.transform(({ chart }) =>
				chart.kind === "GaugeChart" || chart.kind === "StatChart" || chart.kind === "PieChart"
					? undefined
					: (chartWidth ?? 0),
			)
			.getOrElse(0),
		queries: model.transform(prop("metricQueries")).getOrElse([]),
	});

	return (
		<div className={className}>
			<div className="flex flex-wrap justify-between">
				<div className="flex-1 shrink-0">
					<DataCountInformation total={result.transform(({ timeSeries }) => timeSeries.length).getOrElse(undefined)} />
				</div>

				<div className="ml-4 flex grow flex-wrap items-center justify-end gap-4">
					<TimelineRangeSelectorExtended
						contextName="metrics"
						disabled={result.isLoading()}
						timeRange={timeRangeSignal}
					/>

					<RefreshIntervalSelector
						disabled={timeRange.isSpecific() || result.isLoading()}
						loading={isRefetching}
						onChange={setRefreshInterval}
						onRefresh={refetchQueries}
						value={refreshInterval}
					/>

					<SkeletonBox when={model.isSuccess() ? true : null}>
						<LinkTo
							className="button button-secondary gap-2 uppercase"
							disabled={!model.isSuccess()}
							search={({ detachedFilters }) => ({
								timestamp: toDate(timestamp),
								timeRange,
								detachedFilters,
								metrics: {
									queries: model.transform(prop("metricQueries")).getOrElse(undefined)?.map(widgetQueryFromMetricQuery),
									chart: model.transform(prop("chart")).getOrElse(undefined),
								},
							})}
							target="_blank"
							to={METRICS_PATH}
							type="button"
						>
							Explore
							<IconExternalLink className="text-xs/none" />
						</LinkTo>
					</SkeletonBox>
				</div>
			</div>

			<div
				className="mt-4 h-full w-full overflow-hidden rounded-xs border border-grey-300 bg-white p-4"
				ref={measureRef}
			>
				{RemoteData.all({
					timeSeries: result.transform(prop("timeSeries")),
					chart: model.transform(prop("chart")),
				}).match({
					idle: () => <EmptyChart />,

					loading: () => <SkeletonBox className="h-full" />,

					failure: (failure) => <GenericErrorMessage className="h-full" error={failure.error} />,

					success: ({ value: { timeSeries, chart } }) => {
						if (timeSeries.length === 0) {
							return <EmptyChart />;
						}

						return (
							<PaginatedChart
								fallback={<SkeletonBox className="h-full" />}
								legendMaxLines={7}
								markerTimestamp={timestamp}
								onTimeRangeChange={setTimeRange}
								pageSize={20}
								spec={chart}
								timeRange={timeRange}
								timeSeries={timeSeries}
							/>
						);
					},
				})}
			</div>
		</div>
	);
};

const DataCountInformation: React.FC<{
	total: undefined | number;
}> = ({ total }) => (
	<SkeletonText className="flex whitespace-nowrap font-normal text-grey-800 leading-8" when={total}>
		{(x) => {
			const [prefix, count] = x === 0 ? ["There are", "no"] : ["Showing", x];

			return (
				<>
					{prefix} <span className="mx-1 font-medium">{count} time series</span> matching your criteria
				</>
			);
		}}
	</SkeletonText>
);
